import { useQuery } from '@tanstack/react-query';
import { ok, useApi } from './api';
import { useDraft } from './draft';
import type { Bundle, TriggerDef } from './validate';

export type TriggerOption = { value: string; label: string; def?: TriggerDef };

/**
 * Trigger ids and titles from the published content, the local draft, and ids seen in API rows (a code can point at a
 * trigger that has since left the content). Published entries win; codes can only be made for published triggers.
 */
export function useTriggerOptions(seen: { triggerId: string; title?: string | null }[] = []) {
  const { client } = useApi();
  const draft = useDraft();
  const current = useQuery({
    queryKey: ['content-current'],
    queryFn: async () => ok(await client.GET('/content/current')),
  });
  const published = useQuery({
    queryKey: ['admin-content', current.data?.version],
    queryFn: async () =>
      ok(
        await client.GET('/admin/content/{version}', {
          params: { path: { version: current.data?.version ?? 0 } },
        }),
      ),
    enabled: current.data !== undefined,
  });
  const options = new Map<string, TriggerOption>();
  const add = (t: TriggerDef | null | undefined, where: string) => {
    if (!t?.id || options.has(t.id)) return;
    options.set(t.id, {
      value: t.id,
      label: `${t.title || t.id} (${t.id})${where}${t.devOnly ? ' · dev' : ''}`,
      def: t,
    });
  };
  for (const t of (published.data as Bundle | undefined)?.triggers ?? []) add(t, '');
  for (const t of draft?.bundle.triggers ?? []) add(t, ' · draft only');
  for (const r of seen)
    if (!options.has(r.triggerId))
      options.set(r.triggerId, {
        value: r.triggerId,
        label: `${r.title || r.triggerId} (${r.triggerId}) · not in content`,
      });
  return [...options.values()];
}
